import type {
  MatchOperationsEvent,
  MatchOperationsState,
  MatchOperationsTimingSettings,
} from './matchOperations';

export const PUBLIC_MATCH_OPERATIONS_V2_MEDIA_TYPE = 'application/vnd.ysports.public-match-operations.v2+json';

export type PublicMatchOperationStateV2 = {
  sidesSwapped?: boolean;
  revision: number;
  status: string;
  currentPeriod: number;
  clockRunning: boolean;
  periodElapsedAnchorMs: number;
  effectiveElapsedAnchorMs: number;
  clockAnchorAt?: string | null;
  periodDeadlineAt?: string | null;
  scoreTeamA: number;
  scoreTeamB: number;
  teamATimeoutsUsed: number;
  teamBTimeoutsUsed: number;
};

export type PublicMatchOperationConfigurationV2 = {
  regularPeriodCount: number;
  regularPeriodDurationMs: number;
  overtimeEnabled: boolean;
  overtimePeriodCount: number;
  overtimePeriodDurationMs: number;
};

export type PublicMatchOperationSuspensionV2 = {
  sourceEventId: string;
  teamSide: string;
  participantId: string;
  startedAtEffectiveElapsedMs: number;
  durationMs: number;
  expiresAtEffectiveElapsedMs: number;
  status: string;
};

export type PublicMatchOperationEventV2 = {
  id: string;
  revision: number;
  type: string;
  period?: number | null;
  periodElapsedMs?: number | null;
  effectiveElapsedMs?: number | null;
  teamSide?: string | null;
  teamId?: string | null;
  participantId?: string | null;
  targetEventId?: string | null;
  occurredAt: string;
  recordedAt: string;
  shotOutcome?: 'GOAL' | 'SAVED' | 'MISSED' | 'POST' | 'BLOCKED' | null;
  shotType?: 'OPEN_PLAY' | 'SEVEN_METER' | null;
  goalkeeperParticipantId?: string | null;
  defenderParticipantId?: string | null;
};

export type PublicMatchOperationSnapshotV2 = {
  matchId: string;
  operationMode: string;
  configuration: PublicMatchOperationConfigurationV2;
  participants: {
    teamA: { id?: string | null; name?: string | null; players: Array<{ id: string; playerNumber?: number | null; displayName?: string | null }> };
    teamB: { id?: string | null; name?: string | null; players: Array<{ id: string; playerNumber?: number | null; displayName?: string | null }> };
  };
  state: PublicMatchOperationStateV2;
  activeSuspensions: PublicMatchOperationSuspensionV2[];
  revision: number;
  serverTime: string;
};

export type PublicMatchOperationEventPageV2 = {
  fromRevision: number;
  toRevision: number;
  streamRevision: number;
  events: PublicMatchOperationEventV2[];
};

export type PublicMatchOperationDeltaV2 = {
  fromRevision: number;
  toRevision: number;
  events: PublicMatchOperationEventV2[];
  state: PublicMatchOperationStateV2;
  activeSuspensions: PublicMatchOperationSuspensionV2[];
  serverTime: string;
};

const LEGACY_EVENT_TYPES: Record<string, string> = {
  STEAL_RECORDED: 'LOST_BALL_RECORDED',
  GOAL_RECORDED: 'GOAL',
  PENALTY_GOAL_RECORDED: 'PENALTY_GOAL',
  SHOT_MISSED_RECORDED: 'MISSED_7M',
  SHOT_SAVED_RECORDED: 'SHOT_SAVED',
  TEAM_TIMEOUT_RECORDED: 'TEAM_TIMEOUT',
  TWO_MINUTE_SUSPENSION_RECORDED: 'PENALTY_2MIN',
  YELLOW_CARD_RECORDED: 'YELLOW_CARD',
  RED_CARD_RECORDED: 'RED_CARD',
  BLUE_CARD_RECORDED: 'BLUE_CARD',
};

const mapStatus = (status: string) => status === 'FINISHED'
  ? 'ENDED'
  : status === 'PERIOD_COMPLETE'
    ? 'PAUSED'
    : status;

export const toPublicMatchOperationsState = (
  matchId: string,
  state: PublicMatchOperationStateV2,
  suspensions: PublicMatchOperationSuspensionV2[],
  serverTime: string,
  revision = state.revision,
): MatchOperationsState => ({
  sidesSwapped: state.sidesSwapped ?? false,
  matchId,
  status: mapStatus(state.status),
  period: state.currentPeriod,
  elapsedMs: state.periodElapsedAnchorMs,
  periodElapsedMs: state.periodElapsedAnchorMs,
  effectiveElapsedMs: state.effectiveElapsedAnchorMs,
  clockRunning: state.clockRunning,
  scoreTeamA: state.scoreTeamA,
  scoreTeamB: state.scoreTeamB,
  teamATimeouts: state.teamATimeoutsUsed,
  teamBTimeouts: state.teamBTimeoutsUsed,
  lastServerSequence: revision,
  updatedAt: new Date(state.clockAnchorAt || serverTime),
  activeSuspensions: suspensions.map((suspension) => ({
    sourceEventId: suspension.sourceEventId,
    teamSide: suspension.teamSide,
    playerId: suspension.participantId,
    startedAtEffectiveElapsedMs: suspension.startedAtEffectiveElapsedMs,
    durationMs: suspension.durationMs,
    expiresAtEffectiveElapsedMs: suspension.expiresAtEffectiveElapsedMs,
    remainingMs: Math.max(0, suspension.expiresAtEffectiveElapsedMs - state.effectiveElapsedAnchorMs),
    status: suspension.status,
  })),
});

export const toPublicMatchTimingSettings = (
  matchId: string,
  configuration: PublicMatchOperationConfigurationV2,
): MatchOperationsTimingSettings => ({
  matchId,
  regularPeriodCount: configuration.regularPeriodCount,
  regularPeriodDurationMinutes: configuration.regularPeriodDurationMs / 60_000,
  overtimeEnabled: configuration.overtimeEnabled,
  overtimePeriodCount: configuration.overtimePeriodCount,
  overtimePeriodDurationMinutes: configuration.overtimePeriodDurationMs / 60_000,
});

export const toPublicMatchOperationsEvent = (
  matchId: string,
  event: PublicMatchOperationEventV2,
): MatchOperationsEvent => ({
  id: event.id,
  matchId,
  serverSequence: event.revision,
  serverTimestamp: new Date(event.recordedAt),
  type: event.type === 'SHOT_ATTEMPT_RECORDED'
    ? event.shotOutcome === 'GOAL' ? event.shotType === 'SEVEN_METER' ? 'PENALTY_GOAL' : 'GOAL'
      : event.shotOutcome === 'SAVED' ? 'SHOT_SAVED'
        : event.shotOutcome === 'POST' ? 'SHOT_POST'
          : event.shotOutcome === 'BLOCKED' ? 'SHOT_BLOCKED'
            : event.shotType === 'SEVEN_METER' ? 'MISSED_7M' : 'SHOT_MISSED'
    : LEGACY_EVENT_TYPES[event.type] || event.type,
  period: event.period,
  elapsedMs: event.periodElapsedMs,
  teamId: event.teamId,
  teamSide: event.teamSide,
  playerId: event.participantId,
  targetEventId: event.targetEventId,
  shotOutcome: event.shotOutcome,
  shotType: event.shotType,
  goalkeeperPlayerId: event.goalkeeperParticipantId,
  defenderPlayerId: event.defenderParticipantId,
});

const isCorrectionMarker = (event: MatchOperationsEvent) => (
  event.type === 'EVENT_VOIDED' || event.type === 'EVENT_REPLACED'
);

export const mergeEffectivePublicMatchEvent = (
  current: MatchOperationsEvent[],
  incoming: MatchOperationsEvent,
  limit = 500,
): MatchOperationsEvent[] => {
  if (isCorrectionMarker(incoming)) {
    return current.filter((event) => event.id !== incoming.targetEventId);
  }
  if (incoming.id && current.some((event) => event.id === incoming.id)) {
    return current;
  }
  return [...current, incoming]
    .sort((left, right) => (right.serverSequence ?? 0) - (left.serverSequence ?? 0))
    .slice(0, limit);
};

export const effectivePublicMatchEvents = (
  matchId: string,
  events: PublicMatchOperationEventV2[],
  limit = 500,
): MatchOperationsEvent[] => events.reduce(
  (current, event) => mergeEffectivePublicMatchEvent(current, toPublicMatchOperationsEvent(matchId, event), limit),
  [] as MatchOperationsEvent[],
);
